import { FunctionComponent, MouseEvent } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { isBouncedEmailStatus, isSendableEmailStatus } from "@signalist/shared-types";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { EmailIcon } from "@/components/EmailIcon/EmailIcon";
import { getEmailIconStatus } from "@/components/EmailIcon/getEmailIconStatus";
import { PhoneIcon } from "@/components/PhoneIcon/PhoneIcon";
import {
	getPhoneIconStatus,
	hasKnownPhone,
	knownPhoneNumber,
} from "@/components/PhoneIcon/getPhoneIconStatus";
import { IPersonSummary } from "@/pages/Private/redux/search/search.schema";
import {
	revertEnrichPersonSearching,
	setEnrichPersonSearching,
} from "@/pages/Private/redux/search/search.slice";
import {
	useGlobalSearchSearchEmailMutation,
	useGlobalSearchSearchPhoneMutation,
} from "@/pages/Private/redux/search/search.api";
import { translateRequestError } from "@/utils/errors";
import { showGlobalToast } from "@/utils/showGlobalToast";

interface LinkedInPersonChannelsProps {
	person: IPersonSummary;
}

const stop = (e: MouseEvent) => {
	e.preventDefault();
	e.stopPropagation();
};

/**
 * Email + phone reveal icons for a Global Search company-enrich card. Clicking an icon
 * (when not yet revealed) enriches the person + company on the backend and searches the
 * channel; the async result arrives via the `global-search-contact-found` websocket and
 * updates this card by `profileInfoId`.
 */
export const LinkedInPersonChannels: FunctionComponent<LinkedInPersonChannelsProps> = ({
	person,
}) => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [searchEmail] = useGlobalSearchSearchEmailMutation();
	const [searchPhone] = useGlobalSearchSearchPhoneMutation();

	const profileInfoId = String(person.id);

	// A refused reveal (not enough credits, PREC-4437) gives the icon its status back and says why.
	const revertSearch = (
		channel: "EMAIL" | "PHONE",
		status: string | null | undefined,
		error: unknown
	) => {
		dispatch(revertEnrichPersonSearching({ profileInfoId, channel, status }));
		showGlobalToast(translateRequestError(error));
	};

	const costTooltip = (costKey: string) => (
		<div className="flex flex-col gap-1 text-left">
			<span className="font-semibold">{t(costKey)}</span>
			<span>{t("contacts.globalSearchEnrichNote")}</span>
			<span>{t("contacts.globalSearchAvailableNote")}</span>
		</div>
	);

	const emailTooltip =
		person.email && isSendableEmailStatus(person.emailStatus)
			? person.email
			: costTooltip("contacts.globalSearchEmailCost");

	const phoneTooltip = hasKnownPhone(person.phone, person.phoneStatus)
		? person.phone
		: costTooltip("contacts.globalSearchPhoneCost");

	const handleEmailClick = (e: MouseEvent) => {
		stop(e);

		if (person.emailStatus === "SEARCHING") {
			return;
		}

		// A sendable address (VALID, RISKY) and both bounced states keep an address — open it in the
		// mail client rather than kicking off a re-find (a no-op server-side for bounced addresses).
		if (
			person.email &&
			(isSendableEmailStatus(person.emailStatus) || isBouncedEmailStatus(person.emailStatus))
		) {
			window.location.href = `mailto:${person.email}`;

			return;
		}

		dispatch(setEnrichPersonSearching({ profileInfoId, channel: "EMAIL" }));
		searchEmail({ profileInfoId, name: person.name ?? undefined })
			.unwrap()
			.catch((error) => revertSearch("EMAIL", person.emailStatus, error));
	};

	const handlePhoneClick = (e: MouseEvent) => {
		stop(e);

		if (person.phoneStatus === "SEARCHING") {
			return;
		}

		const dialable = knownPhoneNumber(person.phone, person.phoneStatus);

		if (dialable) {
			window.location.href = `tel:${dialable.replace(/\s/g, "")}`;

			return;
		}

		dispatch(setEnrichPersonSearching({ profileInfoId, channel: "PHONE" }));
		searchPhone({ profileInfoId, name: person.name ?? undefined })
			.unwrap()
			.catch((error) => revertSearch("PHONE", person.phoneStatus, error));
	};

	return (
		<div className="flex flex-row items-center" onClick={stop}>
			<Tooltip content={emailTooltip}>
				<button
					aria-label="email"
					className="flex h-[20px] cursor-pointer items-center"
					type="button"
					onClick={handleEmailClick}
				>
					<EmailIcon status={getEmailIconStatus(person.email, person.emailStatus)} />
				</button>
			</Tooltip>

			<Tooltip content={phoneTooltip}>
				<button
					aria-label="phone"
					className="flex h-[20px] cursor-pointer items-center"
					type="button"
					onClick={handlePhoneClick}
				>
					<PhoneIcon status={getPhoneIconStatus(person.phone, person.phoneStatus)} />
				</button>
			</Tooltip>
		</div>
	);
};
